import {
  EmailNotificationNodeData,
  GmailIntegrationData,
  IntegrationsService,
  OrgProfileIntegration,
  OutlookIntegrationData,
  SmtpServer,
  UpdateWorkflowNodeRequest,
  WorkflowNodeType,
  WorkflowsService,
} from '@/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useStore } from '@nanostores/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNodes } from '@xyflow/react';
import { Trash2 as TrashIcon } from 'lucide-react';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FormattedNode } from '../../WorkflowBuilder';
import {
  useAutoSaveDebouncedCallback,
  useNodeConfigurationEventListener,
} from '../utils/nodeConfigurationUtils';
import { TestNodeDrawer } from './TestNodeDrawer';

const googleOAuthClientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
const microsoftOauthClientId = import.meta.env.VITE_MICROSOFT_OAUTH_CLIENT_ID;

export type OAuthEmailIntegrationData =
  | GmailIntegrationData
  | OutlookIntegrationData;

export default function EmailConfiguration({
  selectedNode,
  setNodes,
  readOnly = false,
  setAllRequiredFieldsOfNodeConfigurationFilled,
}: {
  setNodes: any;
  selectedNode: FormattedNode;
  readOnly?: boolean;
  setAllRequiredFieldsOfNodeConfigurationFilled: Dispatch<
    SetStateAction<boolean>
  >;
}) {
  const wF = useStore($workflow);
  const org = useStore($org);
  const nodes = useNodes();
  const queryClient = useQueryClient();
  const [editorKey, setEditorKey] = useState(0);
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);

  // Keep config as it is
  const [smtpConfig, setSmtpConfig] = useState<OrgProfileIntegration | null>(
    null,
  );
  const [fromField, setFromField] = useState<string>('');
  const [toField, setToField] = useState<string>('');
  const [ccList, setCcList] = useState<string[]>([]);
  const [bccList, setBccList] = useState<string[]>([]);
  const [subjectField, setSubjectField] = useState<string>('');
  const [bodyField, setBodyField] = useState<string>('');
  const [loadingOAuthConnectionFlow, setLoadingOAuthConnectionFlow] =
    useState<boolean>(false);
  const [testDrawerOpen, setTestDrawerOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [savedOAuthConnections, setSavedOAuthConnections] = useState<
    OrgProfileIntegration[] | null
  >([]);

  const oauthEmailIntegrationService = new OauthEmailIntegrationService();

  async function saveConfigurations() {
    if (!readOnly) {
      if (!smtpConfig) {
        toast.error('Please connect an email account (Gmail or Outlook) first!');
        return;
      }
      if (!toField) {
        toast.error('Please enter a recipient email address!');
        return;
      }
      if (!subjectField) {
        toast.error('Please enter a subject!');
        return;
      }
      if (!bodyField) {
        toast.error('Please enter a body!');
        return;
      }
      if (!fromField) {
        toast.error('Please select a sender email address!');
        return;
      }
      $savingNodeConfiguration.set(true);
      setAllRequiredFieldsOfNodeConfigurationFilled(true);
      const updatableNode = updateNodeLocally();
      if (updatableNode) {
        try {
          await updateNode.mutateAsync({
            req: {
              data: { ...updatableNode.data },
            },
            nodeId: updatableNode?.id,
          });
          toast.success('Email configuration saved successfully!');
        } catch (error) {
          toast.error('Failed to save email configuration. Please try again.');
          console.error('Error saving email configuration:', error);
        }
      }
      $savingNodeConfiguration.set(false);
    }
  }

  useAutoSaveDebouncedCallback(
    async () => {
      await saveConfigurations();
    },
    2000, // 1 seconds delay
    [smtpConfig, fromField, subjectField, bodyField] as const,
  );

  useNodeConfigurationEventListener('confirmSaveChanges', async detail => {
    await saveConfigurations();
  });

  const {
    data,
    error,
    isLoading: loadingSavedOAuthConnections,
    isFetching: fetchingSavedOAuthConnections,
    refetch: refetchSavedOAuthConnections,
  } = useQuery({
    queryKey: ['savedOAuthConnections', org?.id],
    queryFn: async () => {
      const res = await IntegrationsService.getOrgIntegrations(org?.id || '');
      // filter the email type integrations
      const emailIntegrations = res.filter(
        i =>
          i.integration?.type === 'Gmail' || i.integration?.type === 'Outlook',
      );
      setSavedOAuthConnections(emailIntegrations);
      return res;
    },
    refetchOnMount: true,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });

  async function getOrgProfileOAuthConfig() {
    const res = await IntegrationsService.getOrgProfileOAuthConfig(
      org?.id || '',
    );
    return res;
  }

  async function handleConnectNewAccount(smtpServ: SmtpServer) {
    try {
      setLoadingOAuthConnectionFlow(true);
      let newOrgProfileIntegration;
      if (smtpServ === 'OUTLOOK') {
        newOrgProfileIntegration =
          await oauthEmailIntegrationService.connectOutlook(org!.id);
      }

      if (smtpServ === 'GMAIL') {
        newOrgProfileIntegration =
          await oauthEmailIntegrationService.connectGmail(org!.id);
      }

      if (!newOrgProfileIntegration) {
        setLoadingOAuthConnectionFlow(false);
        return;
      }

      // File specific implementation
      refetchSavedOAuthConnections();
      setSmtpConfig(newOrgProfileIntegration);
      setFromField(
        (newOrgProfileIntegration.integrationData as OAuthEmailIntegrationData)
          ?.email,
      );

      toast.success('Connected successfully!', {
        description: `Your ${newOrgProfileIntegration.integration?.type === 'Gmail' ? 'GMAIL' : 'OUTLOOK'} account has been connected.`,
        duration: 5000,
      });
    } catch (error) {
      toast.error('Connection failed');
    } finally {
      setLoadingOAuthConnectionFlow(false);
    }
  }

  async function createGmailInboxListener(
    accountId: string,
    accessToken: string,
    refreshToken: string,
  ) {
    try {
      const res = await IntegrationsService.createGmailInboxListener({
        accountId,
        accessToken,
        refreshToken,
      });
      return res?.historyId;
    } catch (error) {
      throw new Error('Failed to create Gmail inbox listener');
    }
  }

  function isEmailNotificationNodeData(
    data: any,
  ): data is EmailNotificationNodeData {
    return data.type === 'EMAIL_NOTIFICATION';
  }

  function isGmailIntegrationDataData(data: any): data is GmailIntegrationData {
    return data.type === 'Gmail';
  }

  function isOutlookIntegrationDataData(
    data: any,
  ): data is OutlookIntegrationData {
    return data.type === 'Outlook';
  }

  function isOAuthEmailIntegrationData(
    data: any,
  ): data is OAuthEmailIntegrationData {
    return data.type === 'Outlook' || data.type === 'Gmail'; // Adjust with actual property checks
  }

  function updateNodeLocally() {
    // get the current selected node
    const newNodes = JSON.parse(JSON.stringify(nodes));

    const updatableNode: FormattedNode = newNodes.find(
      (n: any) => n.id === selectedNode.id,
    );

    // find the index of selected node
    const selectedNodeIndex = newNodes.findIndex(
      (n: any) => n.id === selectedNode.id,
    );

    if (updatableNode) {
      updatableNode.data = {
        ...updatableNode.data,
        type: 'EMAIL_NOTIFICATION' as WorkflowNodeType,
      };
      if (isEmailNotificationNodeData(updatableNode.data)) {
        if (smtpConfig) {
          updatableNode.data = {
            ...updatableNode.data,
            email_smtp_integration_id: smtpConfig.id,
          };
        }
        if (fromField) {
          updatableNode.data = {
            ...updatableNode.data,
            from: fromField,
          };
        }
        if (toField) {
          updatableNode.data = {
            ...updatableNode.data,
            to: toField,
          };
        }
        if (ccList.length > 0) {
          updatableNode.data = {
            ...updatableNode.data,
            cc: ccList,
          };
        } else {
          updatableNode.data = {
            ...updatableNode.data,
            cc: null,
          };
        }
        if (bccList.length > 0) {
          updatableNode.data = {
            ...updatableNode.data,
            bcc: bccList,
          };
        } else {
          updatableNode.data = {
            ...updatableNode.data,
            bcc: null,
          };
        }
        if (subjectField) {
          updatableNode.data = {
            ...updatableNode.data,
            subject: subjectField,
          };
        }
        if (bodyField) {
          updatableNode.data = {
            ...updatableNode.data,
            body: bodyField,
          };
        }
      }
      // set nodes
      newNodes[selectedNodeIndex] = updatableNode;
      setNodes(newNodes);
      return updatableNode;
    }
  }

  const updateNode = useMutation({
    mutationFn: async (payload: {
      req: UpdateWorkflowNodeRequest;
      nodeId: string;
    }) => {
      // call the api to create a new workspace
      if (wF) {
        const res = await WorkflowsService.updateWorkflowNode(
          wF.id,
          payload.nodeId,
          payload.req,
        );
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', wF?.id],
      });
      toast.success('Configurations updated!', {
        description: 'Your changes have been saved.',
        duration: 5000,
      });
    },
    onError: error => {
      console.error(error);

      // show a toast message
      toast.error('An error occurred :: Unable to add node.', {
        description: error.message,
        duration: 5000,
      });
    },
  });
  function resetFormFields() {
    setSmtpConfig(null);
    setFromField('');
    setToField('');
    setSubjectField('');
    setBodyField('');
    setCcList([]);
    setBccList([]);
  }

  useEffect(() => {
    async function fetchAndSetConfig() {
      // Reset all form fields first
      resetFormFields();
      
      if (wF) {
        if (wF.type === 'BulkRun') {
          setToField('{UserEmail}');
        }
      }
      
      if (selectedNode) {
        if (isEmailNotificationNodeData(selectedNode.data)) {
          // Force rich text editor to re-render if node changed
          if (currentNodeId !== selectedNode.id) {
            setEditorKey(prev => prev + 1);
            setCurrentNodeId(selectedNode.id);
          }

          // find the email integration
          let selectedodeIntegrationId = selectedNode.data.email_smtp_integration_id;
          const emailIntegration = savedOAuthConnections?.find(
            c => c.id === selectedodeIntegrationId,
          );
          
          if (emailIntegration) {
            setSmtpConfig(emailIntegration);
            if (emailIntegration.integrationData && isOAuthEmailIntegrationData(emailIntegration.integrationData)) {
              setFromField(emailIntegration.integrationData.email || '');
            }
          }
          
          // Set all fields from the node data
          setToField(selectedNode.data.to ?? '');
          setSubjectField(selectedNode.data.subject ?? '');
          setBodyField(selectedNode.data.body ?? '');
          setCcList(selectedNode.data.cc ?? []);
          setBccList(selectedNode.data.bcc ?? []);
        }
      }
    }

    fetchAndSetConfig();
  }, [selectedNode?.id, savedOAuthConnections]);

  useEffect(() => {
    console.log(
      'Updated loadingOAuthConnectionFlow:',
      loadingOAuthConnectionFlow,
    );
  }, [loadingOAuthConnectionFlow]);

  console.log('loadingOAuthConnectionFlow', loadingOAuthConnectionFlow);

  // ---------------------------------------------------------------------
  // UI / JSX
  // ---------------------------------------------------------------------
  return (
    <div className="flex h-full w-full flex-col">
      <div className="h-full flex-1 overflow-y-auto">
        <div className="m-auto mx-auto max-w-sm bg-white p-4">
          {/* Gmail Account Section (unchanged) */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              Email Account
            </label>

            <div className="flex items-center space-x-2">
              <Select
                defaultValue={smtpConfig?.id}
                value={smtpConfig?.id}
                onValueChange={async value => {
                  if (value === 'add_gmail_account') {
                    await handleConnectNewAccount('GMAIL');
                  } else if (value === 'add_outlook_account') {
                    await handleConnectNewAccount('OUTLOOK');
                  } else {
                    // find email from saved oauth connections
                    const connection = savedOAuthConnections?.find(
                      c => c.id === value,
                    );

                    setSmtpConfig(connection || null);
                    if (
                      connection?.integrationData &&
                      isOAuthEmailIntegrationData(connection?.integrationData)
                    ) {
                      setFromField(connection?.integrationData.email || '');
                    }
                  }
                }}
              >
                <SelectTrigger
                  className={`w-[100%] ${readOnly ? 'cursor-not-allowed border-gray-300 bg-gray-200 italic text-gray-500' : ''}`}
                >
                  <SelectValue
                    placeholder={
                      <>
                        {loadingOAuthConnectionFlow ? (
                          <div className="flex animate-pulse justify-start">
                            Connecting authentication servers ...
                          </div>
                        ) : loadingSavedOAuthConnections ? (
                          <div className="flex animate-pulse justify-start">
                            Fetching saved oauth connections ...
                          </div>
                        ) : fetchingSavedOAuthConnections ? (
                          <div className="flex animate-pulse justify-start">
                            Re-fetching saved oauth connections ...
                          </div>
                        ) : (
                          <div className="flex justify-start">
                            Select or add an account
                          </div>
                        )}
                      </>
                    }
                  />
                </SelectTrigger>
                <SelectContent className="w-[100%]">
                  <SelectGroup>
                    <SelectLabel>Mail Server</SelectLabel>
                    {savedOAuthConnections?.map(connection => (
                      <SelectItem
                        value={connection.id}
                        key={connection.id}
                        disabled={
                          loadingOAuthConnectionFlow ||
                          loadingSavedOAuthConnections ||
                          fetchingSavedOAuthConnections
                        }
                      >
                        {loadingOAuthConnectionFlow ? (
                          <div className="flex animate-pulse justify-start">
                            Connecting authentication servers ...
                          </div>
                        ) : loadingSavedOAuthConnections ? (
                          <div className="flex animate-pulse justify-start">
                            Fetching saved oauth connections ...
                          </div>
                        ) : fetchingSavedOAuthConnections ? (
                          <div className="flex animate-pulse justify-start">
                            Re-fetching saved oauth connections ...
                          </div>
                        ) : (
                          <div className="flex justify-start">
                            {connection?.integration?.type === 'Gmail' && (
                              <svg
                                aria-hidden="true"
                                className="mr-2 h-5 w-5"
                                viewBox="0 0 48 48"
                              >
                                <path
                                  fill="#FFC107"
                                  d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
                                ></path>
                                <path
                                  fill="#FF3D00"
                                  d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
                                ></path>
                                <path
                                  fill="#4CAF50"
                                  d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
                                ></path>
                                <path
                                  fill="#1976D2"
                                  d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
                                ></path>
                              </svg>
                            )}

                            {connection?.integration?.type === 'Outlook' && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="mr-2 h-5 w-5"
                                viewBox="0 0 48 48"
                              >
                                <path
                                  fill="#03A9F4"
                                  d="M21,31c0,1.104,0.896,2,2,2h17c1.104,0,2-0.896,2-2V16c0-1.104-0.896-2-2-2H23c-1.104,0-2,0.896-2,2V31z"
                                ></path>
                                <path
                                  fill="#B3E5FC"
                                  d="M42,16.975V16c0-0.428-0.137-0.823-0.367-1.148l-11.264,6.932l-7.542-4.656L22.125,19l8.459,5L42,16.975z"
                                ></path>
                                <path
                                  fill="#0277BD"
                                  d="M27 41.46L6 37.46 6 9.46 27 5.46z"
                                ></path>
                                <path
                                  fill="#FFF"
                                  d="M21.216,18.311c-1.098-1.275-2.546-1.913-4.328-1.913c-1.892,0-3.408,0.669-4.554,2.003c-1.144,1.337-1.719,3.088-1.719,5.246c0,2.045,0.564,3.714,1.69,4.986c1.126,1.273,2.592,1.91,4.378,1.91c1.84,0,3.331-0.652,4.474-1.975c1.143-1.313,1.712-3.043,1.712-5.199C22.869,21.281,22.318,19.595,21.216,18.311z M19.049,26.735c-0.568,0.769-1.339,1.152-2.313,1.152c-0.939,0-1.699-0.394-2.285-1.187c-0.581-0.785-0.87-1.861-0.87-3.211c0-1.336,0.289-2.414,0.87-3.225c0.586-0.81,1.368-1.211,2.355-1.211c0.962,0,1.718,0.393,2.267,1.178c0.555,0.795,0.833,1.895,0.833,3.31C19.907,24.906,19.618,25.968,19.049,26.735z"
                                ></path>
                              </svg>
                            )}
                            {
                              (
                                connection.integrationData as OAuthEmailIntegrationData
                              )?.email
                            }
                          </div>
                        )}
                      </SelectItem>
                    ))}

                    <SelectItem
                      value="add_gmail_account"
                      disabled={loadingOAuthConnectionFlow}
                    >
                      {loadingOAuthConnectionFlow ? (
                        <div className="flex animate-pulse justify-start">
                          Connecting authentication servers ...
                        </div>
                      ) : loadingSavedOAuthConnections ? (
                        <div className="flex animate-pulse justify-start">
                          Fetching saved oauth connections ...
                        </div>
                      ) : fetchingSavedOAuthConnections ? (
                        <div className="flex animate-pulse justify-start">
                          Re-fetching saved oauth connections ...
                        </div>
                      ) : (
                        <div className="flex justify-start">
                          <svg
                            aria-hidden="true"
                            className="mr-2 h-5 w-5"
                            viewBox="0 0 48 48"
                          >
                            <path
                              fill="#FFC107"
                              d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
                            ></path>
                            <path
                              fill="#FF3D00"
                              d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
                            ></path>
                            <path
                              fill="#4CAF50"
                              d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
                            ></path>
                            <path
                              fill="#1976D2"
                              d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
                            ></path>
                          </svg>
                          Add Gmail Account
                        </div>
                      )}
                      {/* {loadingOAuthConnectionFlow
                        ? ' Connecting authentication servers ...'
                        : 'Add Gmail Account'} */}
                    </SelectItem>

                    <SelectItem
                      value="add_outlook_account"
                      disabled={loadingOAuthConnectionFlow}
                    //isabled={true}
                    >
                      {loadingOAuthConnectionFlow ? (
                        <div className="flex animate-pulse justify-start">
                          Connecting authentication servers ...
                        </div>
                      ) : loadingSavedOAuthConnections ? (
                        <div className="flex animate-pulse justify-start">
                          Fetching saved oauth connections ...
                        </div>
                      ) : fetchingSavedOAuthConnections ? (
                        <div className="flex animate-pulse justify-start">
                          Re-fetching saved oauth connections ...
                        </div>
                      ) : (
                        <div className="flex justify-start">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="mr-2 h-5 w-5"
                            viewBox="0 0 48 48"
                          >
                            <path
                              fill="#03A9F4"
                              d="M21,31c0,1.104,0.896,2,2,2h17c1.104,0,2-0.896,2-2V16c0-1.104-0.896-2-2-2H23c-1.104,0-2,0.896-2,2V31z"
                            ></path>
                            <path
                              fill="#B3E5FC"
                              d="M42,16.975V16c0-0.428-0.137-0.823-0.367-1.148l-11.264,6.932l-7.542-4.656L22.125,19l8.459,5L42,16.975z"
                            ></path>
                            <path
                              fill="#0277BD"
                              d="M27 41.46L6 37.46 6 9.46 27 5.46z"
                            ></path>
                            <path
                              fill="#FFF"
                              d="M21.216,18.311c-1.098-1.275-2.546-1.913-4.328-1.913c-1.892,0-3.408,0.669-4.554,2.003c-1.144,1.337-1.719,3.088-1.719,5.246c0,2.045,0.564,3.714,1.69,4.986c1.126,1.273,2.592,1.91,4.378,1.91c1.84,0,3.331-0.652,4.474-1.975c1.143-1.313,1.712-3.043,1.712-5.199C22.869,21.281,22.318,19.595,21.216,18.311z M19.049,26.735c-0.568,0.769-1.339,1.152-2.313,1.152c-0.939,0-1.699-0.394-2.285-1.187c-0.581-0.785-0.87-1.861-0.87-3.211c0-1.336,0.289-2.414,0.87-3.225c0.586-0.81,1.368-1.211,2.355-1.211c0.962,0,1.718,0.393,2.267,1.178c0.555,0.795,0.833,1.895,0.833,3.31C19.907,24.906,19.618,25.968,19.049,26.735z"
                            ></path>
                          </svg>
                          Add Outlook Account
                        </div>
                      )}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* To Field (local state) */}
          <div className="mt-4">
            <label
              htmlFor="to"
              className="block text-sm font-medium text-gray-700"
            >
              To
            </label>
            <Input
              id="to"
              name="to"
              type="email"
              disabled={readOnly || wF?.type === 'BulkRun'}
              value={toField} // local state
              onChange={e => {
                if (!readOnly) {
                  setToField(e.target.value);
                }
              }}
              className={`mt-1 font-semibold text-blue-800 ${readOnly ? 'cursor-not-allowed bg-gray-200 text-gray-500' : ''}`}
              placeholder="Recipient's email address"
            />
          </div>

          {/* CC / BCC Fields (unchanged) */}
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                CC
              </label>
              {ccList.map((cc, index) => (
                <div key={index} className="mt-1 flex items-center">
                  <Input
                    type="email"
                    value={cc}
                    onChange={e => {
                      if (!readOnly) {
                        // update the ccList
                        setCcList(prev => {
                          const newList = [...prev];
                          newList[index] = e.target.value;
                          return newList;
                        });
                      }
                    }}
                    placeholder="CC email"
                    className={`flex-1 ${readOnly ? 'cursor-not-allowed border-gray-300 bg-gray-200 italic text-gray-500' : ''}`}
                  />
                  <Button
                    variant="ghost"
                    className={`ml-2 p-1 text-red-500 hover:text-red-700 ${readOnly ? 'cursor-not-allowed opacity-50' : ''}`}
                    aria-label="Remove CC"
                    disabled={readOnly}
                    onClick={() => {
                      if (!readOnly) {
                        // remove the cc from ccList
                        setCcList(prev => {
                          const newList = [...prev];
                          newList.splice(index, 1);
                          return newList;
                        });
                      }
                    }}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                variant="link"
                onClick={() => {
                  if (!readOnly) {
                    // add a new cc to ccList with empty string
                    setCcList(prev => [...prev, '']);
                  }
                }}
                className="mt-1 text-sm text-blue-500 hover:underline"
              >
                + Add CC
              </Button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                BCC
              </label>
              {bccList.map((bcc, index) => (
                <div key={index} className="mt-1 flex items-center">
                  <Input
                    type="email"
                    value={bcc}
                    onChange={e => {
                      if (!readOnly) {
                        // update the bcc list
                        setBccList(prev => {
                          const newList = [...prev];
                          newList[index] = e.target.value;
                          return newList;
                        });
                      }
                    }}
                    placeholder="BCC email"
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    className="ml-2 p-1 text-red-500 hover:text-red-700"
                    aria-label="Remove BCC"
                    onClick={() => {
                      if (!readOnly) {
                        // remove the bcc from bccList
                        setBccList(prev => {
                          const newList = [...prev];
                          newList.splice(index, 1);
                          return newList;
                        });
                      }
                    }}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                variant="link"
                onClick={() => {
                  if (!readOnly) {
                    // add a new bcc to bccList with empty string
                    setBccList(prev => [...prev, '']);
                  }
                }}
                className="mt-1 text-sm text-blue-500 hover:underline"
              >
                + Add BCC
              </Button>
            </div>
          </div>

          {/* Subject (local state) */}
          <div className="relative mt-4">
            <label
              htmlFor="subject"
              className="block text-sm font-medium text-gray-700"
            >
              Subject
            </label>
            <Input
              id="subject"
              name="subject"
              type="text"
              value={subjectField} // local state
              onChange={e => {
                if (!readOnly) {
                  setSubjectField(e.target.value);
                }
              }}
              className="mt-1"
              placeholder="Short subject line"
            />
          </div>

          {/* Body (local state) */}
          <div className="relative mt-4">
            <label
              htmlFor="body"
              className="block text-sm font-medium text-gray-700"
            >
              Body
            </label>
            <RichTextEditor
              key={editorKey}
              value={bodyField}
              onChange={value => {
                if (!readOnly) {
                  setBodyField(value);
                }
              }}
              readOnly={readOnly}
              placeholder="Write your message here..."
            />
          </div>
          <p className="text-sm text-gray-500">
            Variables: [company_name], [customer_name], [invoice_name],
            [phone_number], [email], [street_address], [city], [state],
            [zip_code], [country], [invoice_amount], [invoice_paid_amount],
            [invoice_balance], [invoice_due_date], [invoice_invoice_date],
            [payment_link], [company_phone_number]
          </p>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              size="sm"
              disabled={readOnly || updateNode.isPending}
              onClick={() => setPreviewOpen(true)}
              className={`px-3 py-1 text-sm${readOnly ? 'cursor-not-allowed bg-gray-200 text-gray-500' : ''}`}
            >
              Preview
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={readOnly || updateNode.isPending}
              onClick={async () => {
                setTestDrawerOpen(true);
              }}
              className={`px-3 py-1 text-sm${readOnly ? 'cursor-not-allowed bg-gray-200 text-gray-500' : ''}`}
            >
              Test
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={`px-3 py-1 text-sm ${readOnly ? 'cursor-not-allowed opacity-50' : ''}`}
              onClick={async () => {
                await saveConfigurations();
              }}
            >
              {updateNode.isPending ? 'Saving' : 'Save'}
            </Button>
          </div>
          <TestNodeDrawer
            open={testDrawerOpen}
            onClose={() => setTestDrawerOpen(false)}
            node={selectedNode}
            workflow={wF}
          />
          <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Email Preview</DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <div>
                  <div className="text-sm font-medium text-gray-500">From</div>
                  <div className="mt-1">{fromField}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">To</div>
                  <div className="mt-1">{toField}</div>
                </div>
                {ccList.length > 0 && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">CC</div>
                    <div className="mt-1">{ccList.join(', ')}</div>
                  </div>
                )}
                {bccList.length > 0 && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">BCC</div>
                    <div className="mt-1">{bccList.join(', ')}</div>
                  </div>
                )}
                <div>
                  <div className="text-sm font-medium text-gray-500">Subject</div>
                  <div className="mt-1">{subjectField}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">Body</div>
                  <div className="mt-1 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: bodyField }} />
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
